import { useCallback, useEffect, useMemo } from 'react';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useDispatch, useSelector } from 'react-redux';

import { getMessage } from '@gef-ui/localization/messages';

import InfoCircleTooltip from 'components/atoms/GefMui/InfoCircleTooltip';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import fetch from 'features/common/fetch';
import { clearFetchStore } from 'features/common/fetch/actions';
import { quickFilter, toggleButtonGroupValues } from 'features/views/dashboard/statistics/constants';
import { getSellectedSeller, getToggleValue } from 'features/views/dashboard/statistics/selectors';
import {
	setBruttoPeriodQuickFilter,
	setSelectedSeller,
	setToggleValue,
} from 'features/views/dashboard/statistics/slice';
import { reset as resetContextUser } from 'features/views/userContext/actions';
import { joinString } from 'utils/string';

import Infographics from './components/InfoGraphics';
import LeadsBar from './components/LeadsBar';
import ResultsTable from './components/ResultsTable';

export const Statistics = () => {
	const dispatch = useDispatch();
	const toggleValue = useSelector(getToggleValue);
	const selectedSeller = useSelector(getSellectedSeller);

	useEffect(() => {
		// resetting possible previous contextUser, which was set through links to tasks/leads/contacts pages
		// and contacts fetchStore, to comply with contacts startup logic
		dispatch(resetContextUser());
		dispatch(clearFetchStore(`${fetch.constants.urls.contactList}.list`));
	}, [dispatch]);

	const handleToggle = useCallback(
		(e, toggleValue) => {
			if (toggleValue !== null) {
				dispatch(setToggleValue(toggleValue));
				dispatch(setSelectedSeller(null));
				dispatch(setBruttoPeriodQuickFilter(quickFilter.TODAY_MINUS_TWO_DAYS));
			}
		},
		[dispatch],
	);

	const selectedButtonSx = {
		textTransform: 'none',
		'&.Mui-selected, &.Mui-selected:hover': {
			background: (theme) => theme.palette.primary.main,
			color: (theme) => theme.palette.common.white,
		},
	};

	const sectionHeaderText = useMemo(() => {
		let context;

		if (selectedSeller) {
			context = joinString(' ', [selectedSeller?.surname, selectedSeller?.firstName]);
		} else {
			context = getMessage('dashboard:statistics.myTeam');
		}

		return joinString(' ', [getMessage('dashboard:statistics.title'), `(${context})`]);
	}, [selectedSeller]);

	return (
		<Paper sx={{ px: 3, py: 2, borderRadius: 1 }}>
			<Stack
				spacing={2}
				sx={{
					pt: 2,
				}}
			>
				<Stack
					direction="row"
					sx={{
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					<Stack
						direction="row"
						sx={{
							alignItems: 'center',
						}}
					>
						<SectionHeader sx={{ fontWeight: 'bold' }} variant="body1" text={sectionHeaderText} />
						<InfoCircleTooltip
							sx={{ fontSize: 15, mb: 1 }}
							text={getMessage('dashboard:statistics.titleTooltip')}
						/>
					</Stack>
					<ToggleButtonGroup
						size="small"
						exclusive
						value={toggleValue}
						onChange={handleToggle}
						sx={{
							mb: 2,
							width: 'fit-content',
							height: '30px',
						}}
					>
						<ToggleButton sx={selectedButtonSx} value={toggleButtonGroupValues.MY_TEAM}>
							{getMessage('dashboard:statistics.businessEvents')}
						</ToggleButton>
						<ToggleButton sx={selectedButtonSx} value={toggleButtonGroupValues.BRUTTO_PROD_TEAM_ACTIVITIES}>
							{getMessage('dashboard:statistics.bruttoProductionAndTeamActivities')}
						</ToggleButton>
					</ToggleButtonGroup>
				</Stack>
				{toggleValue === toggleButtonGroupValues.MY_TEAM && (
					<>
						<Infographics />
						<LeadsBar />
					</>
				)}
				<ResultsTable />
			</Stack>
		</Paper>
	);
};

export default Statistics;
